import { ref, computed } from 'vue';
import { IsDefined, isDefined, isEmpty, isObject } from 'class-validator';
import { isNil } from 'lodash';
import useEventHub from './useEventHub';
import useNotify from './useNotify';
import {
  CustomFieldType,
  CustomFormFlowEntitiesEnum,
  AppsEnum,
  AppointmentStatus,
  CustomFormFlowCategoryEnum,
  getCustomFieldFormattedValue
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import assetVisitService from '@satellite/services/asset-visit-service.js';

export default function useCustomForms(timezone, militaryTimeEnabled, triggers) {
  const activeTrigger = ref(null);
  const customFormsData = ref({});
  const pendingStatusChange = ref(null);
  const eventHub = useEventHub();
  const notify = useNotify();

  const customFieldComponents = computed(() => {
    // Auto generate component names for dynamic importing the Custom Fields components
    const componentsMap = {};
    Object.entries(CustomFieldType).forEach(
      type => (componentsMap[type[1]] = `custom-field-${type[0].toLowerCase()}`)
    );
    return componentsMap;
  });

  const hasActiveTrigger = computed(() => isObject(activeTrigger.value));
  const hasCustomFormsData = computed(
    () => customFormsData.value?.skip === true || customFormsData.value?.length > 0
  );

  /*
   * Fetches the asset containers related to the appointment as pick or drop appointments.
   * For the feature asset container data, the data is related to the asset container id
   * as the objectId.
   */
  const getAppointmentRelatedAssetContainers = async appointmentId => {
    if (appointmentId) {
      const assetContainers = await assetVisitService.getAssetContainersByWarehouseId(null, {
        s: {
          $or: [{ pickupAppointmentId: appointmentId }, { dropAppointmentId: appointmentId }]
        },
        join: []
      });

      return assetContainers || [];
    }
    return [];
  };

  const getCustomFormsData = async (objectId, triggerId, lastChangedDateTimeOnly = false) => {
    const params = {
      s: { objectId, triggerId },
      limit: lastChangedDateTimeOnly ? 1 : 1000
    };
    if (lastChangedDateTimeOnly) {
      params.fields = 'id,lastChangedDateTime,formField.id'; // select the least amount of data possible for counts
    }
    if (objectId && triggerId) {
      const response = await customFormsService.getCustomFormData(params);

      if (lastChangedDateTimeOnly && response.length > 0) {
        return response[0]?.lastChangedDateTime;
      }

      customFormsData.value[triggerId] = response.map(data => {
        data.label = data.formField?.overrideLabel || data.label;
        data.value = getFormattedValue(data);
        return data;
      });
    }
  };

  const getCustomField = formField => {
    return {
      name: formField.id,
      type: formField.field.type,
      label: formField.overrideLabel || formField.field.label,
      order: formField.order,
      description: formField.overrideDescription || formField.field.description,
      placeholder: formField.field.placeholder,
      required: formField.required,
      dropDownValues: formField.field.extraFields?.dropDownValues,
      minLengthOrValue: formField.field.extraFields?.minLengthOrValue,
      maxLengthOrValue: formField.field.extraFields?.maxLengthOrValue
    };
  };

  const createBulkCustomFormsData = async (value, warehouseId) => {
    if (Array.isArray(value)) {
      const firstSubmit = value.every(formData => !formData.id);

      if (firstSubmit) {
        await window.axios.post(`custom-forms/form-data/bulk`, { bulk: value, warehouseId });
        notify(`Appointment data has been updated`, 'success');
        customFormsDataChange();
      } else {
        await updateCustomFormsData(value);
      }
    }
  };

  const updateCustomFormsData = async value => {
    for (const formData of value) {
      if (formData.id) {
        await window.axios.patch(`custom-forms/form-data/${formData.id}`, {
          value: formData.value
        });
      } else if (!isNil(formData.value)) {
        await window.axios.post(`custom-forms/form-data`, { ...formData });
      }
    }

    notify(`Appointment data has been updated`, 'success');
    customFormsDataChange();
  };

  const handleActionFieldTriggered = actionName => {
    if (!actionName) {
      return;
    }
    const featureTrigger = triggers?.value?.find(trigger => trigger.feature === actionName);

    if (featureTrigger) {
      if (featureTrigger.isActive && isDefined(featureTrigger.flow)) {
        setTimeout(() => {
          activeTrigger.value = featureTrigger;
        }, 600);
      } else {
        notify('Feature is not enabled', 'warning');
      }
    } else {
      notify('Feature not found', 'warning');
    }
  };

  const customFormsDataChange = () => {
    eventHub.$emit('customformdata-updated');
    cleanUpCustomFormsData();
  };

  const cleanUpCustomFormsData = triggerId => {
    activeTrigger.value = null;
    pendingStatusChange.value = null;
    customFormsData.value[triggerId] = [];
  };

  const checkForTriggerOnStatus = (status, triggers) => {
    let category = null;

    if (hasCustomFormsData.value || isDefined(pendingStatusChange.value)) {
      return;
    }

    const entityName = CustomFormFlowEntitiesEnum.warehouse;
    const app = AppsEnum.Warehouse;

    if (status === AppointmentStatus.Completed) {
      category = CustomFormFlowCategoryEnum.appointmentStatusCompleted;
    }

    if (status === AppointmentStatus.Arrived) {
      category = CustomFormFlowCategoryEnum.appointmentStatusArrived;
    }

    return triggers?.find(
      trigger =>
        (!app || trigger.app === app) &&
        (!entityName || trigger.entityName === entityName) &&
        trigger.category === category &&
        trigger.isActive &&
        IsDefined(trigger.flow)
    );
  };

  const getFormattedValue = field => {
    if (
      field.value === '' ||
      !isDefined(field.value) ||
      isEmpty(field.value) ||
      (Array.isArray(field.value) && field.value.length === 0)
    ) {
      return '--';
    }
    return getCustomFieldFormattedValue(field, {
      [CustomFieldType.Document]: { generateLink: true },
      [CustomFieldType.MultiDocument]: { generateLink: true },
      [CustomFieldType.Timestamp]: {
        timezone,
        formatAsMilitary: militaryTimeEnabled
      }
    });
  };

  return {
    activeTrigger,
    customFormsData,
    pendingStatusChange,
    hasActiveTrigger,
    getCustomField,
    getFormattedValue,
    createBulkCustomFormsData,
    updateCustomFormsData,
    handleActionFieldTriggered,
    customFormsDataChange,
    cleanUpCustomFormsData,
    checkForTriggerOnStatus,
    getCustomFormsData,
    getAppointmentRelatedAssetContainers,
    customFieldComponents
  };
}
